import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import '../../styles/group/EventCard.css';

/**
 * ClassCardPreview - Shows what the class card will look like before creation
 * This component mirrors the structure of EventCard but as a preview
 */
const ClassCardPreview = ({ classData, isClass = true, uploadedImage = null }) => {
  // Debug log to check what data we're receiving
  console.log('🎓 ClassCardPreview received data:', classData);
  console.log('🎓 allow_sponsorship value:', classData?.allow_sponsorship);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBD';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const weekday = date.toLocaleString('default', { weekday: 'short' }).toUpperCase();
    return `${weekday} ${day} ${month}`;
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return 'Time TBD';
    const options = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleTimeString(undefined, options);
  };

  // Format recurring display
  const formatRecurringDisplay = (isRecurring, recurrencePattern, recurrenceDay, dateString) => {
    if (!isRecurring || !recurrencePattern || recurrencePattern === 'none') {
      return dateString ? `${formatDate(dateString)}, ${formatTime(dateString)}` : 'Schedule TBD';
    }
    
    const time = dateString ? formatTime(dateString) : 'Time TBD';
    
    if (recurrencePattern === 'daily') {
      return `Every Day at ${time}`;
    } else if (recurrencePattern === 'weekly' && recurrenceDay) {
      const days = {
        'monday': 'Monday',
        'tuesday': 'Tuesday', 
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday',
        'sunday': 'Sunday'
      };
      const dayName = days[recurrenceDay.toLowerCase()] || recurrenceDay;
      return `Every ${dayName} at ${time}`;
    }
    
    return dateString ? `${formatDate(dateString)}, ${formatTime(dateString)}` : 'Schedule TBD';
  };

  // Format recurrence tag
  const formatRecurrenceTag = (isRecurring, recurrencePattern, isClass) => {
    if (!isRecurring || !recurrencePattern || recurrencePattern === 'none') {
      return isClass ? 'Class' : 'Event';
    }
    
    const patternMap = {
      'daily': 'Daily',
      'weekly': 'Weekly', 
      'biweekly': 'Bi-weekly'
    };
    
    const formattedPattern = patternMap[recurrencePattern.toLowerCase()] || 
                            recurrencePattern.charAt(0).toUpperCase() + recurrencePattern.slice(1);
    
    return `${formattedPattern} ${isClass ? 'Class' : 'Event'}`;
  };

  // Truncate description to prevent card from breaking
  const truncateDescription = (text, maxLength = 150) => {
    if (!text) return 'No description provided';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <div className="event-card preview-card" style={{ 
      border: '2px dashed #4682B4', 
      position: 'relative',
      width: '100%',
      alignSelf: 'stretch'
    }}>
      {/* Preview badge */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        background: '#4682B4',
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        zIndex: 10
      }}>
        PREVIEW
      </div>

      {/* Cover image section */}
      <div className="event-card-image-container">
        {/* Card type tag in top left corner */}
        <div className="event-card-type-tag">
          {formatRecurrenceTag(classData.is_recurring, classData.recurrence_pattern, isClass)}
        </div>

        {/* Show uploaded image or placeholder */}
        {uploadedImage ? (
          <img 
            src={uploadedImage} 
            alt="Class cover" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <div className="event-card-placeholder" style={{
            backgroundColor: isClass ? '#4682B4' : '#DAA520',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '2.5rem'
          }}>
            <FontAwesomeIcon icon={isClass ? faBookOpen : faCalendarAlt} />
          </div>
        )}
      </div>
      
      {/* Content section */}
      <div className="event-card-content">
        {/* Title */}
        <h3 className="event-card-title">{classData.title || 'Class Title TBD'}</h3>
        
        {/* Date and time with teacher */}
        <div className="event-card-datetime">
          {formatRecurringDisplay(classData.is_recurring, classData.recurrence_pattern, classData.recurrence_day, classData.date)}
          {classData.teacher && <span className="event-card-type"> | {classData.teacher}</span>}
        </div>
        
        {/* Description */}
        <p className="event-card-description">
          {truncateDescription(classData.description)}
        </p>
        
        {/* Preview action area */}
        <div className="event-card-actions">
          <button 
            className="sponsor-button" 
            disabled 
            style={{ 
              opacity: classData.allow_sponsorship ? 1 : 0.3,
              backgroundColor: classData.allow_sponsorship ? '#3b82f6' : '#e5e7eb',
              color: classData.allow_sponsorship ? 'white' : '#6b7280',
              cursor: 'not-allowed'
            }}
          >
            Dedicate
          </button>
          
          <div className="event-card-attendees">
            <div className="attendees-count" style={{ opacity: 0.6 }}>
              0 going
            </div>
            <div className="attendees-avatars">
              <div className="attendee-avatar empty-avatar"></div>
              <div className="attendee-avatar empty-avatar"></div>
              <div className="attendee-avatar empty-avatar"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassCardPreview;
